import { useState, useEffect } from "react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";

// Stripe integration
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

// API function instead of service import
const getApiBase = () => {
  return import.meta.env.DEV
    ? "http://localhost:8787"
    : "https://shopglobal-backend-production.finhub.workers.dev";
};

const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const token = localStorage.getItem("shopglobal_auth_token");
  const defaultHeaders: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    defaultHeaders.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${getApiBase()}${endpoint}`, {
    ...options,
    headers: { ...defaultHeaders, ...options.headers },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || "API call failed");
  }

  return data;
};

interface Address {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface ServicePlan {
  id: string;
  name: string;
  price: number;
  billingCycle: "monthly" | "yearly";
  features: string[];
  stripePriceId: string;
}

interface OnboardingStatus {
  isOnboardingComplete: boolean;
  completedSteps: string[];
  currentStep: string;
  forwardingAddress?: Address;
  selectedPlan?: ServicePlan;
  servicePlans: ServicePlan[];
}

interface OnboardingWizardProps {
  onComplete: () => void;
}

export function OnboardingWizard({ onComplete }: OnboardingWizardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState(0);
  const [address, setAddress] = useState<Address>({
    street: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  });
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");

  // State for onboarding status and loading
  const [onboardingStatus, setOnboardingStatus] =
    useState<OnboardingStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveAddressLoading, setSaveAddressLoading] = useState(false);
  const [selectPlanLoading, setSelectPlanLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Load onboarding status on component mount
  useEffect(() => {
    loadOnboardingStatus();
  }, []);

  const loadOnboardingStatus = async () => {
    try {
      const data = await apiCall("/api/onboarding/status");
      setOnboardingStatus(data);

      if (data.isOnboardingComplete) {
        onComplete();
        return;
      }

      // Set current step based on completed steps
      if (data.completedSteps.includes("payment")) {
        setCurrentStep(2);
      } else if (data.completedSteps.includes("plan")) {
        setCurrentStep(2); // After plan selection, go to payment step
      } else if (data.completedSteps.includes("address")) {
        setCurrentStep(1);
      } else {
        setCurrentStep(0);
      }

      // Pre-fill address if available
      if (data.forwardingAddress) {
        setAddress(data.forwardingAddress);
      }

      // Pre-fill selected plan if available
      if (data.selectedPlan) {
        setSelectedPlanId(data.selectedPlan.id);
      }
    } catch (err: any) {
      setError(err.message || "Failed to load onboarding status");
    } finally {
      setIsLoading(false);
    }
  };

  // Save address
  const saveAddress = async (addressData: Address) => {
    setSaveAddressLoading(true);
    setError(null);
    try {
      await apiCall("/api/onboarding/address", {
        method: "POST",
        body: JSON.stringify(addressData),
      });
      alert("Address saved successfully!");
      setCurrentStep(1);
      loadOnboardingStatus(); // Reload status
    } catch (err: any) {
      setError(err.message || "Failed to save address");
    } finally {
      setSaveAddressLoading(false);
    }
  };

  // Select plan
  const selectPlan = async (planId: string) => {
    setSelectPlanLoading(true);
    setError(null);
    try {
      await apiCall("/api/onboarding/plan", {
        method: "POST",
        body: JSON.stringify({ planId }),
      });
      alert("Plan selected successfully!");
      setCurrentStep(2);
      loadOnboardingStatus(); // Reload status
    } catch (err: any) {
      setError(err.message || "Failed to select plan");
    } finally {
      setSelectPlanLoading(false);
    }
  };

  // Create checkout session
  const createCheckout = async (planId: string) => {
    setPaymentLoading(true);
    setError(null);
    console.log("🔄 Creating checkout for plan:", planId);
    try {
      const data = await apiCall("/api/onboarding/checkout", {
        method: "POST",
        body: JSON.stringify({ planId }),
      });
      console.log("💰 Checkout response:", data);

      if (data.checkoutUrl) {
        console.log("🚀 Redirecting to Stripe:", data.checkoutUrl);
        // Complete onboarding and redirect to Stripe
        window.location.href = data.checkoutUrl;
      } else if (data.success) {
        // Demo mode - simulate successful payment
        console.log("💡 Demo mode: Simulating successful payment");
        alert(
          "🎉 Payment successful (Demo Mode)! Congratulations on completing your ShopGlobal Pro setup!"
        );
        // Complete onboarding by redirecting to dashboard
        onComplete();
      } else {
        console.error("❌ No checkoutUrl in response:", data);
        setError("Failed to create checkout session");
      }
    } catch (err: any) {
      console.error("💥 Checkout error:", err);
      setError(err.message || "Failed to create checkout");
    } finally {
      setPaymentLoading(false);
    }
  };

  const steps = [
    { title: "Address Setup", description: "Set up your forwarding address" },
    { title: "Choose Plan", description: "Select your service plan" },
    { title: "Complete Payment", description: "Secure your subscription" },
  ];

  const handleAddressSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await saveAddress(address);
  };

  const handlePlanSubmit = async () => {
    if (selectedPlanId) {
      await selectPlan(selectedPlanId);
    }
  };

  const handlePaymentSubmit = async () => {
    if (selectedPlanId) {
      await createCheckout(selectedPlanId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-red-600 mb-4">
            <svg
              className="mx-auto h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => {
              setError(null);
              loadOnboardingStatus();
            }}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6 text-center">
        <h1 className="text-3xl font-bold text-gray-900">
          ShopGlobal Pro Setup
        </h1>
      </div>

      {/* Progress Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {steps.map((step, index) => (
            <div key={step.title} className="flex items-center">
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  index <= currentStep
                    ? "bg-blue-600 border-blue-600 text-white"
                    : "border-gray-300 text-gray-300"
                }`}>
                {index < currentStep ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <span className="text-sm font-semibold">{index + 1}</span>
                )}
              </div>
              <div className="ml-3">
                <p
                  className={`text-sm font-semibold ${
                    index <= currentStep ? "text-gray-900" : "text-gray-500"
                  }`}>
                  {step.title}
                </p>
                <p className="text-xs text-gray-500 mt-1">{step.description}</p>
              </div>
              {index < steps.length - 1 && (
                <ArrowRight className="h-4 w-4 mx-8 text-gray-300" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8">
        {currentStep === 0 && (
          /* Address Setup Step */
          <form onSubmit={handleAddressSubmit} className="space-y-4">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold">Set Up Forwarding Address</h2>
              <p className="text-gray-600 mt-2">
                Provide your Canadian address where you'd like your packages
                delivered
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Street Address
              </label>
              <input
                type="text"
                value={address.street}
                onChange={(e) =>
                  setAddress((prev) => ({ ...prev, street: e.target.value }))
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="123 Main St"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  value={address.city}
                  onChange={(e) =>
                    setAddress((prev) => ({ ...prev, city: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Anytown"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State/Province
                </label>
                <input
                  type="text"
                  value={address.state}
                  onChange={(e) =>
                    setAddress((prev) => ({ ...prev, state: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="ON"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ZIP/Postal Code
                </label>
                <input
                  type="text"
                  value={address.zipCode}
                  onChange={(e) =>
                    setAddress((prev) => ({ ...prev, zipCode: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="A1B 2C3"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  type="text"
                  value={address.country}
                  onChange={(e) =>
                    setAddress((prev) => ({ ...prev, country: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Canada"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button
                type="submit"
                disabled={saveAddressLoading}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center">
                {saveAddressLoading && (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                )}
                Continue to Plan Selection
                <ArrowRight className="h-4 w-4 ml-2" />
              </button>
            </div>
          </form>
        )}

        {currentStep === 1 && (
          /* Plan Selection Step */
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold">Choose Your Plan</h2>
              <p className="text-gray-600 mt-2">
                Select the plan that best fits your needs
              </p>
            </div>

            {onboardingStatus?.servicePlans.map((plan) => (
              <div
                key={plan.id}
                className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                  selectedPlanId === plan.id
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => setSelectedPlanId(plan.id)}>
                <div className="flex items-center space-x-4">
                  <input
                    type="radio"
                    value={plan.id}
                    checked={selectedPlanId === plan.id}
                    onChange={() => setSelectedPlanId(plan.id)}
                    className="w-4 h-4 text-blue-600"
                  />

                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-semibold">{plan.name}</h3>
                      <span className="text-lg font-bold text-blue-600">
                        ${plan.price}/{plan.billingCycle}
                      </span>
                    </div>

                    <ul className="text-sm text-gray-600 space-y-1">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-center">
                          <Check className="h-3 w-3 text-green-500 mr-2" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}

            <div className="flex justify-between pt-4">
              <button
                onClick={() => setCurrentStep(0)}
                disabled={selectPlanLoading}
                className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Address
              </button>

              <button
                onClick={handlePlanSubmit}
                disabled={!selectedPlanId || selectPlanLoading}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center">
                {selectPlanLoading && (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                )}
                Proceed to Payment
                <ArrowRight className="h-4 w-4 ml-2" />
              </button>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          /* Payment Step with Embedded Stripe Elements */
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold mb-2">
                Secure Payment Information
              </h2>
              <p className="text-gray-600 mb-4">
                Enter your payment details below to complete your subscription
              </p>

              {onboardingStatus?.selectedPlan && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-w-sm mx-auto mb-6">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">
                      {onboardingStatus.selectedPlan.name}
                    </span>
                    <span className="font-bold text-blue-600">
                      ${onboardingStatus.selectedPlan.price}/
                      {onboardingStatus.selectedPlan.billingCycle}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Stripe Elements Payment Form */}
            <StripeElementsWrapper
              planId={selectedPlanId}
              amount={onboardingStatus?.selectedPlan?.price || 0}
              billingCycle={
                onboardingStatus?.selectedPlan?.billingCycle || "monthly"
              }
              onPaymentSuccess={() => {
                onComplete(); // Complete onboarding and redirect to dashboard
              }}
              onPaymentError={(error) => setError(error)}
            />

            <div className="flex justify-between pt-4">
              <button
                onClick={() => setCurrentStep(1)}
                disabled={paymentLoading}
                className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Plan
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Stripe Elements Card Style
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "16px",
      color: "#374151",
      "::placeholder": {
        color: "#9CA3AF",
      },
      fontFamily: "Inter, system-ui, sans-serif",
      padding: "12px 16px",
    },
    invalid: {
      color: "#EF4444",
    },
  },
};

// Real Stripe Payment Form Component
const StripeElementsCardElement: React.FC<{
  planId: string;
  amount: number;
  billingCycle: string;
  onPaymentSuccess: () => void;
  onPaymentError: (error: string) => void;
}> = ({ planId, amount, billingCycle, onPaymentSuccess, onPaymentError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      onPaymentError("Stripe not loaded. Please refresh and try again.");
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      onPaymentError("Card element not found.");
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Create PaymentMethod
      const { error: methodError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
        });

      if (methodError) {
        setError(methodError.message || "Invalid card details");
        return;
      }

      // Send payment method to our backend
      const response = await apiCall("/api/onboarding/create-payment-intent", {
        method: "POST",
        body: JSON.stringify({
          planId,
          paymentMethodId: paymentMethod.id,
        }),
      });

      if (response.error) {
        setError(response.error);
        return;
      }

      // Confirm PaymentIntent if returned
      if (response.clientSecret) {
        const { error: confirmError } = await stripe.confirmCardPayment(
          response.clientSecret
        );
        if (confirmError) {
          setError(confirmError.message || "Payment failed");
          return;
        }
      }

      console.log("💳 Payment successful with Stripe Elements!");
      onPaymentSuccess();
    } catch (err: any) {
      console.error("💥 Stripe payment error:", err);
      setError(err.message || "Payment processing failed");
    } finally {
      setProcessing(false);
    }
  };

  if (!planId || amount <= 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        Loading payment information...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Stripe Card Element */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Card Information
          </label>
          <div className="border border-gray-300 rounded-lg p-3 focus-within:ring-2 focus-within:ring-blue-500">
            <CardElement
              options={CARD_ELEMENT_OPTIONS}
              onChange={(event) => {
                if (event.error) {
                  setError(event.error.message);
                } else {
                  setError(null);
                }
              }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Enter your card number, expiry date, CVC, and postal code
          </p>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!stripe || processing}
          className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center">
          {processing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Processing Payment...
            </>
          ) : (
            <>
              Pay ${amount} {billingCycle}
            </>
          )}
        </button>
      </form>

      {/* Security Notice */}
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center text-sm text-gray-600">
          <svg
            className="w-4 h-4 mr-2 text-green-500"
            fill="currentColor"
            viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
              clipRule="evenodd"
            />
          </svg>
          🔒 Your payment information is secure and PCI compliant
          <br />
          <span className="text-xs">Powered by Stripe</span>
        </div>
      </div>
    </div>
  );
};

// Stripe Elements Wrapper Component
const StripeElementsWrapper: React.FC<{
  planId: string;
  amount: number;
  billingCycle: string;
  onPaymentSuccess: () => void;
  onPaymentError: (error: string) => void;
}> = ({ planId, amount, billingCycle, onPaymentSuccess, onPaymentError }) => {
  // Load Stripe instance
  const stripePromise = React.useMemo(() => {
    // Real Stripe test key provided by user
    const publishableKey = import.meta.env.DEV
      ? "pk_test_51RpFpKALLnLXWzyNSlvpYHidOm8Eh0bpgKIDeeJNIO43zir8Z6M0P7flEhNvI3EvwZoEqUzCbZ3vEp6VT8ZEqCtt00HiWDi9bX" // User's real test key
      : (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ??
        "pk_test_51RpFpKALLnLXWzyNSlvpYHidOm8Eh0bpgKIDeeJNIO43zir8Z6M0P7flEhNvI3EvwZoEqUzCbZ3vEp6VT8ZEqCtt00HiWDi9bX");

    console.log(
      "📌 Loading Stripe with key:",
      publishableKey.substring(0, 10) + "..."
    );
    return loadStripe(publishableKey);
  }, []);

  if (!stripePromise) {
    return (
      <div className="text-center py-8 text-gray-500">
        Unable to load payment system. Please refresh and try again.
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <StripeElementsCardElement
        planId={planId}
        amount={amount}
        billingCycle={billingCycle}
        onPaymentSuccess={onPaymentSuccess}
        onPaymentError={onPaymentError}
      />
    </Elements>
  );
};

// Helper components for the wizard
const AddressSetup: React.FC = () => <div>Address Setup</div>;
const PlanSelection: React.FC = () => <div>Plan Selection</div>;
const PaymentStep: React.FC = () => <div>Payment Step</div>;

export default OnboardingWizard;
